import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'OmbudNow';
const DEFAULT_LOCALE = 'pt_BR';
const SITE_URL = 'https://ombudnow.com';

/**
 * SEO Component for dynamic meta tags and structured data
 * 
 * @param {string} title - Page title (will append site name)
 * @param {string} description - Meta description (150-160 chars ideal)
 * @param {string} canonical - Canonical URL path (e.g., '/pt/sobre')
 * @param {string} lang - Language code ('pt' or 'en')
 * @param {string} ogType - Open Graph type (default: 'website')
 * @param {string} ogImage - Open Graph image URL
 * @param {array} schemas - Array of JSON-LD schema objects
 * @param {boolean} noindex - Set true for pages that shouldn't be indexed
 */
export default function SEO({
  title,
  description,
  canonical,
  lang = 'pt',
  ogType = 'website',
  ogImage = '/og-image.png',
  schemas = [],
  noindex = false,
  keywords = ''
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const locale = lang === 'en' ? 'en_US' : 'pt_BR';
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content={SITE_NAME} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      
      {/* JSON-LD Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

// ==================== SCHEMA GENERATORS ====================

/**
 * Organization Schema
 */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "OmbudNow",
  "url": SITE_URL,
  "logo": `${SITE_URL}/logo.png`,
  "description": "Plataforma SaaS de Canal de Denúncias e Ouvidoria para empresas",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "São Paulo",
    "addressRegion": "SP",
    "addressCountry": "BR"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "support@ombudnow.com",
    "availableLanguage": ["Portuguese", "English"]
  },
  "sameAs": []
};

/**
 * Software Application Schema
 */
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "OmbudNow",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "AggregateOffer",
    "lowPrice": "89.00",
    "highPrice": "499.00",
    "priceCurrency": "BRL",
    "offerCount": "3"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "150"
  },
  "description": "Sistema completo de Canal de Denúncias e Ouvidoria Corporativa para empresas de todos os tamanhos.",
  "featureList": [
    "Canal de Denúncias Anônimo",
    "Ouvidoria Corporativa",
    "Dashboard Administrativo",
    "Gestão de Casos",
    "Relatórios Automáticos",
    "Conformidade com LGPD"
  ]
};

/**
 * FAQ Page Schema Generator
 * @param {array} faqs - Array of {question, answer} objects
 */
export const createFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Breadcrumb Schema Generator
 * @param {array} items - Array of {name, url} objects
 */
export const createBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `${SITE_URL}${item.url}`
  }))
});

/**
 * Article Schema Generator
 * @param {object} article - Article data
 */
export const createArticleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.description,
  "image": article.image || `${SITE_URL}/og-image.png`,
  "author": {
    "@type": "Organization",
    "name": "OmbudNow"
  },
  "publisher": {
    "@type": "Organization",
    "name": "OmbudNow",
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_URL}/logo.png`
    }
  },
  "datePublished": article.datePublished || new Date().toISOString(),
  "dateModified": article.dateModified || new Date().toISOString()
});

/**
 * WebPage Schema Generator
 */
export const createWebPageSchema = (page) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": page.name,
  "description": page.description,
  "url": `${SITE_URL}${page.url}`,
  "isPartOf": {
    "@type": "WebSite",
    "name": "OmbudNow",
    "url": SITE_URL
  },
  "breadcrumb": page.breadcrumb ? createBreadcrumbSchema(page.breadcrumb) : undefined
});

/**
 * Service Schema Generator (for solution pages)
 */
export const createServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": service.name,
  "description": service.description,
  "provider": {
    "@type": "Organization",
    "name": "OmbudNow"
  },
  "serviceType": service.type || "Compliance Software",
  "areaServed": {
    "@type": "Country",
    "name": service.country || "Brazil"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Planos OmbudNow",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Plano Essencial"
        },
        "price": "89.00",
        "priceCurrency": "BRL"
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Plano Profissional"
        },
        "price": "199.00",
        "priceCurrency": "BRL"
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Plano Empresarial"
        },
        "price": "499.00",
        "priceCurrency": "BRL"
      }
    ]
  }
});
